/**
 * ASNPlusAdminTab - Admin management for ASN Plus features
 * Sub-tabs: Inbox, Newsletters, Polls, Pick'em
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  MessageSquare, Mail, Vote, Trophy, Send, Loader2, Trash2, Plus, X,
  Check, ChevronRight, Clock, Users, Crown, BarChart3, Lock, Eye, Paperclip,
  UserCheck, UserX, Search
} from 'lucide-react';
import { Button } from '../ui/button';
import { API, authHeaders } from './utils';

// ============================================
// INBOX SUB-TAB
// ============================================
const InboxSection = () => {
  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchConvos = useCallback(async () => {
    try {
      const res = await fetch(`${API}/plus/inbox/admin/all`, { headers: authHeaders() });
      if (res.ok) setConvos(await res.json());
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConvos(); }, [fetchConvos]);

  const selectConvo = async (userId) => {
    try {
      const res = await fetch(`${API}/plus/inbox/admin/conversation/${userId}`, { headers: authHeaders() });
      if (res.ok) { setSelected(await res.json()); fetchConvos(); }
    } catch {}
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await fetch(`${API}/plus/inbox/admin/reply/${selected.user_id}`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: reply })
      });
      setReply('');
      selectConvo(selected.user_id);
    } catch {} finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;

  return (
    <div className="flex gap-4 h-[500px]" data-testid="admin-inbox">
      {/* Conversation List */}
      <div className="w-64 border border-white/5 bg-zinc-900/50 rounded-lg overflow-y-auto flex-shrink-0">
        <div className="p-3 border-b border-white/5">
          <h4 className="text-white text-xs font-bold uppercase">Conversations ({convos.length})</h4>
        </div>
        {convos.length === 0 ? (
          <p className="p-4 text-zinc-600 text-xs text-center">No messages yet</p>
        ) : convos.map(c => (
          <button key={c.user_id} onClick={() => selectConvo(c.user_id)}
            className={`w-full text-left p-3 border-b border-white/5 transition-colors ${
              selected?.user_id === c.user_id ? 'bg-white/5' : 'hover:bg-white/5'
            }`}>
            <div className="flex items-center justify-between">
              <span className="text-white text-xs font-medium truncate">{c.user_name || c.user_email}</span>
              {c.admin_unread_count > 0 && (
                <span className="bg-orange-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full">{c.admin_unread_count}</span>
              )}
            </div>
            <p className="text-zinc-600 text-[10px] mt-0.5 truncate">{c.user_email}</p>
          </button>
        ))}
      </div>

      {/* Message Thread */}
      <div className="flex-1 border border-white/5 bg-zinc-900/50 rounded-lg flex flex-col">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center"><p className="text-zinc-600 text-xs">Select a conversation</p></div>
        ) : (
          <>
            <div className="p-3 border-b border-white/5 flex items-center justify-between">
              <div>
                <span className="text-white text-sm font-bold">{selected.user_name}</span>
                <span className="text-zinc-600 text-xs ml-2">{selected.user_email}</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {(selected.messages || []).map(msg => (
                <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] px-3 py-2 rounded-lg text-xs ${
                    msg.sender === 'admin' ? 'bg-orange-500/20 text-orange-200' : 'bg-zinc-800 text-zinc-300'
                  }`}>
                    {msg.subject && <p className="font-bold mb-1">{msg.subject}</p>}
                    <p>{msg.content}</p>
                    <p className="text-[9px] text-zinc-600 mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-white/5 flex gap-2">
              <input value={reply} onChange={e => setReply(e.target.value)} placeholder="Type a reply..."
                className="flex-1 bg-zinc-800 border border-white/10 text-white text-xs px-3 py-2 rounded"
                onKeyDown={e => e.key === 'Enter' && sendReply()} data-testid="admin-reply-input" />
              <Button onClick={sendReply} disabled={sending || !reply.trim()} className="bg-orange-500 hover:bg-orange-600 text-xs" data-testid="admin-reply-btn">
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// NEWSLETTERS SUB-TAB
// ============================================
const NewslettersSection = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [subCount, setSubCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', summary: '' });
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/plus/newsletters/admin/all`, { headers: authHeaders() });
      if (res.ok) { const d = await res.json(); setNewsletters(d.newsletters || []); setSubCount(d.subscriber_count || 0); }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const uploadFile = async () => {
    if (!fileRef.current?.files?.[0]) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', fileRef.current.files[0]);
      const res = await fetch(`${API}/plus/newsletters/admin/upload-attachment`, {
        method: 'POST', headers: { 'Authorization': authHeaders()['Authorization'] },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setAttachments(prev => [...prev, data.attachment]);
      }
      if (fileRef.current) fileRef.current.value = '';
    } catch {} finally { setUploading(false); }
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const sendNewsletter = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSending(true);
    try {
      await fetch(`${API}/plus/newsletters/admin/create`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, attachments })
      });
      setForm({ title: '', content: '', summary: '' });
      setAttachments([]);
      setShowForm(false);
      fetchData();
    } catch {} finally { setSending(false); }
  };

  const deleteNl = async (id) => {
    if (!window.confirm('Delete this newsletter?')) return;
    await fetch(`${API}/plus/newsletters/admin/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchData();
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-newsletters">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400 text-xs"><Users className="w-3 h-3 inline mr-1" />{subCount} Plus subscriber{subCount !== 1 ? 's' : ''}</p>
        <Button onClick={() => setShowForm(!showForm)} className="bg-orange-500 hover:bg-orange-600 text-xs" data-testid="new-newsletter-btn">
          <Plus className="w-3 h-3 mr-1" /> New Newsletter
        </Button>
      </div>

      {showForm && (
        <div className="bg-zinc-900/50 border border-orange-500/20 rounded-lg p-4 space-y-3">
          <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Newsletter title"
            className="w-full bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded" data-testid="nl-title" />
          <input value={form.summary} onChange={e => setForm({...form, summary: e.target.value})} placeholder="Summary (optional)"
            className="w-full bg-zinc-800 border border-white/10 text-white text-xs px-3 py-2 rounded" />
          <textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} placeholder="Newsletter content..."
            className="w-full bg-zinc-800 border border-white/10 text-white text-xs px-3 py-2 rounded h-32 resize-y" data-testid="nl-content" />

          {/* File Attachments */}
          <div className="border border-white/5 rounded p-3 space-y-2">
            <p className="text-zinc-500 text-[10px] uppercase font-medium">Attachments</p>
            {attachments.length > 0 && (
              <div className="space-y-1">
                {attachments.map((att, i) => (
                  <div key={att.id || i} className="flex items-center gap-2 text-xs bg-zinc-800/50 rounded px-3 py-1.5">
                    <Paperclip className="w-3 h-3 text-orange-400 flex-shrink-0" />
                    <span className="text-zinc-300 flex-1 truncate">{att.original_name}</span>
                    <span className="text-zinc-600">{att.size_kb} KB</span>
                    <button onClick={() => removeAttachment(i)} className="text-zinc-500 hover:text-red-400"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" className="hidden" onChange={uploadFile} data-testid="nl-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline"
                className="text-xs border-white/10 text-zinc-400 hover:text-white" data-testid="nl-attach-btn">
                {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Paperclip className="w-3 h-3 mr-1" />}
                {uploading ? 'Uploading...' : 'Attach File'}
              </Button>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={sendNewsletter} disabled={sending || !form.title || !form.content} className="bg-orange-500 hover:bg-orange-600 text-xs" data-testid="send-newsletter-btn">
              {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Send to All Subscribers
            </Button>
            <Button onClick={() => setShowForm(false)} variant="ghost" className="text-xs text-zinc-400"><X className="w-3 h-3" /></Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {newsletters.length === 0 ? (
          <div className="bg-zinc-900/30 border border-white/5 rounded-lg p-8 text-center">
            <Mail className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No newsletters sent yet</p>
          </div>
        ) : newsletters.map(nl => (
          <div key={nl.id} className="flex items-center gap-4 p-3 bg-zinc-900/50 border border-white/5 rounded-lg group">
            <Mail className="w-5 h-5 text-zinc-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-white text-sm font-medium">{nl.title}</span>
              {nl.summary && <p className="text-zinc-500 text-xs mt-0.5 truncate">{nl.summary}</p>}
              <div className="flex items-center gap-3 mt-0.5">
                <p className="text-zinc-600 text-[10px]">{new Date(nl.sent_at).toLocaleString()}</p>
                {(nl.attachments || []).length > 0 && (
                  <span className="text-zinc-600 text-[10px] flex items-center gap-1"><Paperclip className="w-2.5 h-2.5" />{nl.attachments.length} file{nl.attachments.length !== 1 ? 's' : ''}</span>
                )}
              </div>
            </div>
            <button onClick={() => deleteNl(nl.id)} className="p-1.5 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// POLLS SUB-TAB
// ============================================
const PollsSection = () => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [question, setQuestion] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [creating, setCreating] = useState(false);

  const fetchPolls = useCallback(async () => {
    try {
      const res = await fetch(`${API}/plus/polls/admin/all`, { headers: authHeaders() });
      if (res.ok) setPolls(await res.json());
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPolls(); }, [fetchPolls]);

  const createPoll = async () => {
    const validOptions = options.filter(o => o.trim());
    if (!question.trim() || validOptions.length < 2) return;
    setCreating(true);
    try {
      await fetch(`${API}/plus/polls/admin/create`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, description, options: validOptions.map(t => ({ text: t })) })
      });
      setQuestion(''); setDescription(''); setOptions(['', '']); setShowForm(false);
      fetchPolls();
    } catch {} finally { setCreating(false); }
  };

  const closePoll = async (id) => {
    await fetch(`${API}/plus/polls/admin/${id}/close`, { method: 'POST', headers: authHeaders() });
    fetchPolls();
  };

  const deletePoll = async (id) => {
    if (!window.confirm('Delete this poll?')) return;
    await fetch(`${API}/plus/polls/admin/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchPolls();
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-polls">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(!showForm)} className="bg-orange-500 hover:bg-orange-600 text-xs" data-testid="new-poll-btn">
          <Plus className="w-3 h-3 mr-1" /> New Poll
        </Button>
      </div>

      {showForm && (
        <div className="bg-zinc-900/50 border border-orange-500/20 rounded-lg p-4 space-y-3">
          <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="Poll question"
            className="w-full bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded" data-testid="poll-question" />
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)"
            className="w-full bg-zinc-800 border border-white/10 text-white text-xs px-3 py-2 rounded" />
          <div className="space-y-2">
            {options.map((opt, i) => (
              <div key={i} className="flex gap-2">
                <input value={opt} onChange={e => { const o = [...options]; o[i] = e.target.value; setOptions(o); }}
                  placeholder={`Option ${i + 1}`} className="flex-1 bg-zinc-800 border border-white/10 text-white text-xs px-3 py-2 rounded" />
                {options.length > 2 && (
                  <button onClick={() => setOptions(options.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400"><X className="w-3 h-3" /></button>
                )}
              </div>
            ))}
            <button onClick={() => setOptions([...options, ''])} className="text-orange-500 text-xs hover:text-orange-400">+ Add Option</button>
          </div>
          <div className="flex gap-2">
            <Button onClick={createPoll} disabled={creating || !question.trim() || options.filter(o => o.trim()).length < 2}
              className="bg-orange-500 hover:bg-orange-600 text-xs" data-testid="create-poll-btn">
              {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Vote className="w-3 h-3 mr-1" />} Create Poll
            </Button>
            <Button onClick={() => setShowForm(false)} variant="ghost" className="text-xs text-zinc-400"><X className="w-3 h-3" /></Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {polls.length === 0 ? (
          <div className="bg-zinc-900/30 border border-white/5 rounded-lg p-8 text-center">
            <Vote className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No polls created yet</p>
          </div>
        ) : polls.map(poll => (
          <div key={poll.id} className="bg-zinc-900/50 border border-white/5 rounded-lg p-4 group">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-white text-sm font-bold">{poll.question}</h4>
              <div className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 text-[10px] rounded ${poll.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                  {poll.status}
                </span>
                <span className="text-zinc-600 text-[10px]">{poll.total_votes} vote{poll.total_votes !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="space-y-1.5 mb-3">
              {poll.options.map(opt => {
                const pct = poll.total_votes > 0 ? Math.round(opt.votes / poll.total_votes * 100) : 0;
                return (
                  <div key={opt.id} className="relative">
                    <div className="flex items-center justify-between text-xs py-1.5 px-3 rounded bg-zinc-800 relative z-10">
                      <span className="text-zinc-300">{opt.text}</span>
                      <span className="text-zinc-500">{opt.votes} ({pct}%)</span>
                    </div>
                    <div className="absolute inset-0 bg-orange-500/10 rounded" style={{ width: `${pct}%` }} />
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              {poll.status === 'active' && (
                <button onClick={() => closePoll(poll.id)} className="text-zinc-500 hover:text-orange-400 text-[10px]">Close Poll</button>
              )}
              <button onClick={() => deletePoll(poll.id)} className="text-zinc-500 hover:text-red-400 text-[10px] ml-auto">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// PICK'EM SUB-TAB
// ============================================
const PickemSection = () => {
  const [weeks, setWeeks] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');
  const [selectedGames, setSelectedGames] = useState([]);
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [wRes, bRes] = await Promise.all([
        fetch(`${API}/plus/pickem/admin/weeks`, { headers: authHeaders() }),
        fetch(`${API}/broadcasts/schedule?include_dummy=true`, { headers: authHeaders() })
      ]);
      if (wRes.ok) setWeeks(await wRes.json());
      if (bRes.ok) {
        const data = await bRes.json();
        setBroadcasts(data.filter(b => !b.is_completed));
      }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createWeek = async () => {
    if (!title.trim() || selectedGames.length === 0 || !deadline) return;
    setCreating(true);
    try {
      await fetch(`${API}/plus/pickem/admin/weeks`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description, game_ids: selectedGames, deadline: new Date(deadline).toISOString() })
      });
      setTitle(''); setDescription(''); setDeadline(''); setSelectedGames([]); setShowForm(false);
      fetchData();
    } catch {} finally { setCreating(false); }
  };

  const gradeWeek = async (id) => {
    await fetch(`${API}/plus/pickem/admin/weeks/${id}/grade`, { method: 'POST', headers: authHeaders() });
    fetchData();
  };

  const deleteWeek = async (id) => {
    if (!window.confirm('Delete this week and all picks?')) return;
    await fetch(`${API}/plus/pickem/admin/weeks/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchData();
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-pickem">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(!showForm)} className="bg-orange-500 hover:bg-orange-600 text-xs" data-testid="new-pickem-btn">
          <Plus className="w-3 h-3 mr-1" /> New Week
        </Button>
      </div>

      {showForm && (
        <div className="bg-zinc-900/50 border border-orange-500/20 rounded-lg p-4 space-y-3">
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Week title (e.g., Week 1)"
            className="w-full bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded" data-testid="pickem-title" />
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)"
            className="w-full bg-zinc-800 border border-white/10 text-white text-xs px-3 py-2 rounded" />
          <div>
            <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Deadline</label>
            <input type="datetime-local" value={deadline} onChange={e => setDeadline(e.target.value)}
              className="w-64 bg-zinc-800 border border-white/10 text-white text-xs px-3 py-2 rounded" data-testid="pickem-deadline" />
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Select Games ({selectedGames.length} selected)</label>
            <div className="max-h-48 overflow-y-auto space-y-1 bg-zinc-800/50 border border-white/5 rounded p-2">
              {broadcasts.length === 0 ? (
                <p className="text-zinc-600 text-xs p-2">No upcoming broadcasts</p>
              ) : broadcasts.map(b => (
                <label key={b.id} className="flex items-center gap-2 p-1.5 hover:bg-white/5 rounded cursor-pointer text-xs">
                  <input type="checkbox" checked={selectedGames.includes(b.id)}
                    onChange={e => setSelectedGames(e.target.checked ? [...selectedGames, b.id] : selectedGames.filter(g => g !== b.id))}
                    className="rounded" />
                  <span className="text-zinc-300">{b.home_team?.abbreviation || '?'} vs {b.away_team?.abbreviation || '?'}</span>
                  <span className="text-zinc-600 ml-auto">{b.scheduled_date}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={createWeek} disabled={creating || !title || selectedGames.length === 0 || !deadline}
              className="bg-orange-500 hover:bg-orange-600 text-xs" data-testid="create-pickem-btn">
              {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trophy className="w-3 h-3 mr-1" />} Create Week
            </Button>
            <Button onClick={() => setShowForm(false)} variant="ghost" className="text-xs text-zinc-400"><X className="w-3 h-3" /></Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {weeks.length === 0 ? (
          <div className="bg-zinc-900/30 border border-white/5 rounded-lg p-8 text-center">
            <Trophy className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No Pick'em weeks created yet</p>
          </div>
        ) : weeks.map(w => (
          <div key={w.id} className="bg-zinc-900/50 border border-white/5 rounded-lg p-4 group">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-white text-sm font-bold">{w.title}</h4>
              <div className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 text-[10px] rounded ${w.status === 'active' ? 'bg-green-500/20 text-green-400' : w.status === 'graded' ? 'bg-orange-500/20 text-orange-400' : 'bg-zinc-700 text-zinc-400'}`}>
                  {w.status}
                </span>
                <span className="text-zinc-600 text-[10px]">{w.pick_count || 0} pick{(w.pick_count || 0) !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <p className="text-zinc-500 text-xs mb-2">{(w.games || []).length} games &bull; Deadline: {new Date(w.deadline).toLocaleString()}</p>
            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              {w.status === 'active' && (
                <button onClick={() => gradeWeek(w.id)} className="text-orange-500 hover:text-orange-400 text-[10px]">Grade Week</button>
              )}
              <button onClick={() => deleteWeek(w.id)} className="text-zinc-500 hover:text-red-400 text-[10px] ml-auto">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// USERS MANAGEMENT SUB-TAB
// ============================================
const UsersSection = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [acting, setActing] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/subscriptions/admin/users`, { headers: authHeaders() });
      if (res.ok) setUsers(await res.json());
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const grantPlus = async (userId) => {
    setActing(userId);
    try {
      await fetch(`${API}/subscriptions/admin/users/${userId}/grant-plus`, {
        method: 'POST', headers: authHeaders()
      });
      fetchUsers();
    } catch {} finally { setActing(null); }
  };

  const revokePlus = async (userId) => {
    if (!window.confirm('Revoke ASN Plus access? If they have a Stripe subscription, it will be cancelled.')) return;
    setActing(userId);
    try {
      await fetch(`${API}/subscriptions/admin/users/${userId}/revoke-plus`, {
        method: 'POST', headers: authHeaders()
      });
      fetchUsers();
    } catch {} finally { setActing(null); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;

  const filtered = search
    ? users.filter(u => (u.name || '').toLowerCase().includes(search.toLowerCase()) || (u.email || '').toLowerCase().includes(search.toLowerCase()))
    : users;

  const plusUsers = users.filter(u => u.is_plus);

  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400 text-xs">{users.length} total user{users.length !== 1 ? 's' : ''} &bull; {plusUsers.length} Plus subscriber{plusUsers.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..."
          className="w-full bg-zinc-800 border border-white/10 text-white text-xs px-3 py-2 pl-9 rounded" data-testid="user-search" />
      </div>

      <div className="space-y-1 max-h-[500px] overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-zinc-600 text-xs text-center py-4">No users found</p>
        ) : filtered.map(user => (
          <div key={user.id} className="flex items-center gap-3 p-3 bg-zinc-900/50 border border-white/5 rounded-lg group hover:border-white/10 transition-colors">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs font-bold text-zinc-400 flex-shrink-0">
              {(user.name || user.email || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white text-sm font-medium truncate">{user.name || 'Unnamed'}</span>
                {user.is_plus && (
                  <span className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] rounded bg-orange-500/20 text-orange-400 font-bold" data-testid={`plus-badge-${user.id}`}>
                    <Crown className="w-2.5 h-2.5" /> PLUS
                  </span>
                )}
                {user.plus_source === 'admin_granted' && user.is_plus && (
                  <span className="px-1.5 py-0.5 text-[9px] rounded bg-violet-500/20 text-violet-400">FREE</span>
                )}
                {user.role === 'developer' && (
                  <span className="px-1.5 py-0.5 text-[9px] rounded bg-blue-500/20 text-blue-400">DEV</span>
                )}
              </div>
              <p className="text-zinc-600 text-[10px] truncate">{user.email}</p>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {user.is_plus ? (
                <button onClick={() => revokePlus(user.id)} disabled={acting === user.id}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] text-red-400 hover:bg-red-500/10 rounded transition-colors"
                  data-testid={`revoke-plus-${user.id}`}>
                  {acting === user.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserX className="w-3 h-3" />}
                  Revoke
                </button>
              ) : (
                <button onClick={() => grantPlus(user.id)} disabled={acting === user.id}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] text-orange-400 hover:bg-orange-500/10 rounded transition-colors"
                  data-testid={`grant-plus-${user.id}`}>
                  {acting === user.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
                  Grant Plus
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// MAIN TAB COMPONENT
// ============================================
export const ASNPlusAdminTab = () => {
  const [activeTab, setActiveTab] = useState('inbox');

  const tabs = [
    { id: 'inbox', label: 'Inbox', icon: MessageSquare },
    { id: 'newsletters', label: 'Newsletters', icon: Mail },
    { id: 'polls', label: 'Polls', icon: Vote },
    { id: 'pickem', label: "Pick'em", icon: Trophy },
    { id: 'users', label: 'Users', icon: Users }
  ];

  return (
    <div className="space-y-6" data-testid="asn-plus-admin-tab">
      <div>
        <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
          <img src="/asn-plus-logo.png" alt="ASN+" className="w-8 h-8 object-contain" />
          ASN Plus Manager
        </h2>
        <p className="text-zinc-500 text-sm">Manage subscriber inbox, newsletters, polls, and Pick'em contests</p>
      </div>

      <div className="flex gap-1 border-b border-white/10 overflow-x-auto scrollbar-hide">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              activeTab === t.id ? 'text-white border-orange-500' : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`} data-testid={`plus-tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'inbox' && <InboxSection />}
      {activeTab === 'newsletters' && <NewslettersSection />}
      {activeTab === 'polls' && <PollsSection />}
      {activeTab === 'pickem' && <PickemSection />}
      {activeTab === 'users' && <UsersSection />}
    </div>
  );
};

export default ASNPlusAdminTab;
